/**
 * Content for the Slow Plate homepage.
 *
 * Names and descriptions are read from the live menu (thaprachanMenu.ts) and
 * prices from fullMenu.json, so a menu edit updates the homepage too. Only the
 * things the menu doesn't carry live here: Thai script, photography, and the
 * one-word note under each caption.
 */
import fullMenu from './fullMenu.json';
import { getMenuItems } from './menuLookup';
import { dishes } from './dishes';

/**
 * Drop a muted, looping clip into public/ and set its path here, e.g.
 * '/video/thaprachan-hero.mp4'. The hero then plays it instead of the photo
 * slideshow, using the first slide as its poster.
 */
export const HERO_VIDEO_SRC: string | null = null;

/** Ids in fullMenu.json that differ from the id in thaprachanMenu.ts. */
const PRICE_IDS: Record<string, string> = {
  'tom-yum-talay-noodle': 'tom-yum-talay',
};

const priceIndex = new Map<string, number>();
for (const section of fullMenu as { items: { id: string; basePrice?: number }[] }[]) {
  for (const item of section.items) {
    if (typeof item.basePrice === 'number') priceIndex.set(item.id, item.basePrice);
  }
}

export const priceFor = (menuId: string) => priceIndex.get(PRICE_IDS[menuId] ?? menuId);
export const formatPrice = (n?: number) => (n === undefined ? '' : `$${n.toFixed(2)}`);

export type LookMark = 'chili' | 'veg' | 'season';

export interface Look {
  menuId: string;
  name: string;
  description: string;
  price?: number;
  /** Thai script, only where the site's dish pages already publish it. */
  thai?: string;
  /** Dish landing page, when one exists. */
  href?: string;
  mark: { kind: LookMark; label: string };
  image: { src: string; desktop?: string; alt: string; posD?: string; posM?: string };
}

interface LookSeed extends Omit<Look, 'name' | 'description' | 'price' | 'thai'> {
  /** Shorter display name than the menu's, where the menu's is long. */
  displayName?: string;
}

const enc = (path: string) => encodeURI(path);

// Spice marks only where the menu's own description names chili or heat. The
// kitchen hasn't published heat levels, so nothing here claims one.
const SEEDS: LookSeed[] = [
  {
    menuId: 'khao-soi',
    href: '/dishes/khao-soi',
    mark: { kind: 'chili', label: 'Chili oil' },
    image: {
      src: enc('/images/menu-photos/Khao Soy.webp'),
      alt: 'Khao Soi in a blue-patterned bowl, crispy noodles and cilantro on top, with sliced onion and chili on the side',
      posD: '50% 42%',
      posM: '50% 36%',
    },
  },
  {
    menuId: 'pad-thai',
    href: '/dishes/pad-thai',
    mark: { kind: 'veg', label: 'Tofu or vegetable' },
    image: {
      src: enc('/images/menu-photos/Pad Thai.webp'),
      desktop: enc('/images/menu-photos/optimized/Pad Thai.webp'),
      alt: 'Pad Thai with shrimp, egg, crushed peanuts and scallion on a hand-painted plate',
      posD: '50% 55%',
      posM: '50% 45%',
    },
  },
  {
    menuId: 'green-curry',
    mark: { kind: 'veg', label: 'Tofu or vegetable' },
    image: {
      src: '/images/menu-photos/GreenCurry.webp',
      desktop: '/images/menu-photos/optimized/GreenCurry.webp',
      alt: 'Green curry with basil in a ceramic bowl, rice alongside',
      posD: '50% 58%',
      posM: '30% 60%',
    },
  },
  {
    menuId: 'thai-papaya-salad',
    mark: { kind: 'chili', label: 'Chili-lime' },
    image: {
      src: enc('/images/menu-photos/Papaya Salad.webp'),
      alt: 'Thai papaya salad with tomatoes, string beans and carrot on a yellow plate',
      posM: '50% 55%',
    },
  },
  {
    menuId: 'tom-yum-talay-noodle',
    displayName: 'Tom Yum Talay Noodle',
    mark: { kind: 'chili', label: 'Hot and sour' },
    image: {
      src: enc('/images/menu-photos/Tom Yum Talay Noodle.webp'),
      alt: 'Tom Yum Talay noodle soup with mussels, shrimp, crushed peanuts, lime and chopsticks',
      posM: '40% 50%',
    },
  },
  {
    menuId: 'mango-sticky-rice',
    href: '/dishes/mango-sticky-rice',
    mark: { kind: 'season', label: 'Seasonal mango' },
    image: {
      src: enc('/images/menu-photos/Mango with Sticky Rice.webp'),
      alt: 'Mango with sticky rice, sesame seeds and purple flowers on a white plate',
      posM: '50% 60%',
    },
  },
];

const menu = new Map(getMenuItems(SEEDS.map((s) => s.menuId)).map((item) => [item.id, item]));

/** Every look whose dish is still on the menu, in homepage order. */
export const looks: Look[] = SEEDS.flatMap(({ displayName, ...seed }) => {
  const item = menu.get(seed.menuId);
  if (!item) return [];
  const thai = seed.href ? dishes.find((d) => `/dishes/${d.slug}` === seed.href)?.thaiScript : undefined;
  return [{ ...seed, thai, name: displayName ?? item.name, description: item.description, price: priceFor(seed.menuId) }];
});

export interface HeroSlide {
  /** Caption shown in the counter. */
  label: { desktop: string; mobile: string };
  alt: { desktop: string; mobile: string };
  desktop: string;
  mobile: string;
  posD?: string;
  posM?: string;
}

/**
 * Wide originals on desktop, portrait dish photos on phones, so nothing is
 * upscaled. The counter names whichever photo is actually on screen.
 */
export const heroSlides: HeroSlide[] = [
  {
    label: { desktop: 'Green Curry', mobile: 'Khao Soi' },
    alt: {
      desktop: 'Green curry in a ceramic bowl beside a bowl of rice on a dark wood table',
      mobile: 'Khao Soi: egg noodles in curry topped with crispy noodles and cilantro',
    },
    desktop: '/images/menu-photos/optimized/GreenCurry.webp',
    mobile: enc('/images/menu-photos/Khao Soy.webp'),
    posD: '50% 56%',
    posM: '50% 38%',
  },
  {
    label: { desktop: 'Pad Thai', mobile: 'Pad Thai' },
    alt: {
      desktop: 'Pad Thai with shrimp, crushed peanuts and bean sprouts on a hand-painted plate',
      mobile: 'Pad Thai with shrimp, crushed peanuts and bean sprouts on a hand-painted plate',
    },
    desktop: enc('/images/menu-photos/optimized/Pad Thai.webp'),
    mobile: enc('/images/menu-photos/Pad Thai.webp'),
    posD: '50% 52%',
  },
  {
    label: { desktop: 'Tapas', mobile: 'Tom Yum Talay' },
    alt: {
      desktop: 'Overhead spread of tapas plates on woven placemats with purple flowers',
      mobile: 'Tom Yum Talay noodle soup with mussels, shrimp, crushed peanuts and lime',
    },
    desktop: '/images/tapas/optimized/IMG_4162.webp',
    mobile: enc('/images/menu-photos/Tom Yum Talay Noodle.webp'),
    posM: '42% 50%',
  },
  {
    label: { desktop: 'Catering', mobile: 'Mango Sticky Rice' },
    alt: {
      desktop: 'Catering trays of curry, stir-fry and fried rice seen from above',
      mobile: 'Mango with sticky rice and sesame seeds on a white plate',
    },
    desktop: enc('/images/Menu Photos /optimized/Catering.webp'),
    mobile: enc('/images/menu-photos/Mango with Sticky Rice.webp'),
    posD: '50% 42%',
    posM: '50% 58%',
  },
  {
    label: { desktop: 'At the Wok', mobile: 'Papaya Salad' },
    alt: {
      desktop: 'Chef preparing authentic Thai cuisine at a flaming wok',
      mobile: 'Thai papaya salad with tomatoes, string beans and carrots',
    },
    desktop: '/images/menu-photos/optimized/ChefImage.webp',
    mobile: enc('/images/menu-photos/Papaya Salad.webp'),
    posD: '50% 38%',
  },
];

export interface TapasPoster {
  name: string;
  line: string;
  src: string;
}

export const tapasPosters: TapasPoster[] = [
  { name: 'Chive Cha Cha', line: 'Flat chives, black soy dipping sauce', src: '/assets/images/tapas-posters/chive-cha-cha.webp' },
  { name: 'Shrimp Donut', line: 'Shrimp cakes, sweet chili', src: '/assets/images/tapas-posters/shrimp-donut.webp' },
  { name: 'Oh My Gyo', line: 'Fried chicken-stuffed wonton, sweet chili', src: '/assets/images/tapas-posters/oh-my-gyo.webp' },
  { name: 'One-Sun Beef', line: 'Beef jerky, Thai sriracha', src: '/assets/images/tapas-posters/one-sun-beef.webp' },
  { name: 'Pixxie Calamari', line: 'Calamari, creamy spicy herb dressing', src: '/assets/images/tapas-posters/pixxie-calamari.webp' },
  { name: 'Soft Shell 805', line: 'Soft shell crab, black pepper garlic sauce', src: '/assets/images/tapas-posters/soft-shell-805.webp' },
];

export const instagramPosts = [
  { href: 'https://www.instagram.com/thaprachanthai/reel/DHr2sMgP5uB/', src: '/images/instagram/optimized/post1.webp', label: 'Instagram reel from Thaprachan Thai' },
  { href: 'https://www.instagram.com/thaprachanthai/p/DIH0GymhGxO/', src: '/images/instagram/optimized/post2.webp', label: 'Instagram post from Thaprachan Thai' },
  { href: 'https://www.instagram.com/thaprachanthai/p/DHuTr9Wy_Hi/', src: '/images/instagram/optimized/post3.webp', label: 'Instagram post from Thaprachan Thai' },
];
